import { useEffect, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { getEmployeeProfilePicture } from './employees';

export const EMPLOYEE_PROFILE_PICTURE_KEY = 'EmployeeProfilePicture';

/**
 * Returns a temporary object URL for the employee's profile picture, or null
 * when no picture is uploaded (404) or no userId is set. The URL is revoked
 * on unmount and when the underlying blob changes.
 */
export const useGetEmployeeProfilePicture = (
  userId: string | null | undefined,
) => {
  const { data: blob, ...rest } = useQuery({
    queryKey: [EMPLOYEE_PROFILE_PICTURE_KEY, userId],
    queryFn: () => getEmployeeProfilePicture(userId!),
    enabled: !!userId,
    // A 404 means "no picture uploaded" — a normal outcome, not worth retrying.
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const objectUrl = useMemo(
    () => (blob ? URL.createObjectURL(blob) : null),
    [blob],
  );

  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  return { ...rest, data: objectUrl };
};
